// CTSK-268 slice B — document-level YAML edit modal for the registry
// tab surfaces.
//
// WHY A WHOLE-DOC MODAL AND NOT PER-ROW EDITING: the brief's slice-B
// escalation trigger fired. No existing endpoint exposes per-row
// YAML-list mutation — PATCH /documents/:id only replaces the whole
// `content` string (documents.ts), POST /tools/:id/registry is a
// stub, and POST /tools/:id/registry/append (CTSK-251) is append-only
// by design. Per-row editing client-side would also force a
// yaml.dump() round-trip of the WHOLE file, destroying the
// load-bearing comment headers the registry files carry (the exact
// hazard CTSK-251 engineered around). So: the operator edits the raw
// YAML text — comments and formatting survive verbatim — and the save
// replaces the document content through the EXISTING update endpoint.
// A per-row edit surface can land later behind a real server-side
// row-mutation endpoint (separate CTSK).
//
// Concurrency: single-writer semantics per the ticket's non-goals. We
// fetch the document row on open to capture its ETag and send it as
// If-Match; a mid-flight change by someone else returns 409 and the
// operator is told to reopen (no silent last-write-wins).

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, apiSingle } from '../lib/api.js';
import { toast } from './Toast.js';

interface DocRow {
  id: string;
  etag?: string;
}

export interface DocumentYamlEditModalProps {
  documentId: string;
  /** Registry file path, e.g. 'events.yaml' — display only. */
  path: string;
  /** The YAML text currently rendered in the tab's table. */
  initialYaml: string;
  onClose: () => void;
}

export function DocumentYamlEditModal({ documentId, path, initialYaml, onClose }: DocumentYamlEditModalProps) {
  const qc = useQueryClient();
  const [text, setText] = useState(initialYaml);

  // Fresh row fetch on open — the ETag is the If-Match token for the
  // save. Read-only refetch; the textarea keeps showing the yaml the
  // operator was already looking at in the table.
  const rowQ = useQuery({
    queryKey: ['document-row', documentId],
    queryFn: () => apiSingle<DocRow>(`/documents/${documentId}`),
  });

  const saveMut = useMutation({
    mutationFn: () =>
      apiSingle<DocRow>(`/documents/${documentId}`, {
        method: 'PATCH',
        body: { content: text },
        ifMatch: rowQ.data?.etag,
      }),
    onSuccess: () => {
      toast.success(`${path} saved — registry updated`);
      // The tab body re-renders from the content query; the activity
      // panel (slice C) refreshes from the audit trail.
      qc.invalidateQueries({ queryKey: ['tool-registry-content'] });
      qc.invalidateQueries({ queryKey: ['document-activity', documentId] });
      qc.invalidateQueries({ queryKey: ['document-row', documentId] });
      onClose();
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError && err.status === 409) {
        toast.error(
          `${path} changed since you opened the editor — close and reopen to pick up the latest version, then re-apply your edit.`,
        );
        return;
      }
      toast.fromError(err, `Failed to save ${path}`);
    },
  });

  // ESC closes (unless a save is in flight).
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saveMut.isPending) onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose, saveMut.isPending]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Edit ${path}`}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        style={{
          background: 'var(--surface-raised)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--border-strong)',
          width: 'min(860px, 100%)',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          padding: 16,
          gap: 12,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
          <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>
            Edit{' '}
            <code style={{ fontFamily: 'var(--font-mono)', background: 'var(--surface-sunken)', padding: '1px 5px', borderRadius: 4 }}>
              {path}
            </code>
          </h2>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            Whole-document edit — comments and formatting are preserved exactly as typed.
          </span>
        </div>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          aria-label={`YAML content of ${path}`}
          spellCheck={false}
          style={{
            flex: 1,
            minHeight: '50vh',
            resize: 'vertical',
            fontFamily: 'var(--font-mono)',
            fontSize: 12,
            lineHeight: 1.5,
            padding: 10,
            border: '1px solid var(--border-strong)',
            borderRadius: 6,
            background: 'var(--surface-sunken)',
            color: 'var(--text-primary)',
            whiteSpace: 'pre',
          }}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            type="button"
            onClick={onClose}
            disabled={saveMut.isPending}
            style={{ padding: '6px 14px', background: 'var(--surface-raised)', color: 'var(--text-secondary)', border: '1px solid var(--border-strong)', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => saveMut.mutate()}
            disabled={saveMut.isPending || rowQ.isLoading}
            style={{ padding: '6px 14px', background: 'var(--accent-primary)', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: saveMut.isPending || rowQ.isLoading ? 0.6 : 1 }}
          >
            {saveMut.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
